/**
 * The policy and information pages, as content rather than templates.
 *
 * Written against what this site actually does: the models that store visitor data, the two Google
 * scripts that load on every page, and the affiliate redirect. A generic template would be worse
 * than nothing here, because it would describe collection that never happens and miss the
 * collection that does. Every specific claim below was checked against the code first, and the
 * comments say which file it came from.
 *
 * Written without dashes as connectors, too. A dash is the easy way to bolt a second thought onto a
 * sentence, and a page full of them reads as though nobody went back and finished the sentences.
 */
export interface LegalSection {
  heading: string;
  /** Paragraphs and lists, in order. Plain strings are paragraphs. */
  body: (string | { list: string[] })[];
}

export interface LegalDocument {
  path: string;
  title: string;
  metaTitle: string;
  metaDescription: string;
  intro: string;
  /** Shown to readers. */
  updated: string;
  /** The same date for `dateModified` in the page's schema, which needs ISO 8601. */
  updatedIso: string;
  sections: LegalSection[];
}

/** One place, so five documents cannot drift apart on the date. */
const LAST_UPDATED = '24 August 2026';
const LAST_UPDATED_ISO = '2026-08-24';

const CONTACT_EMAIL = 'help@savingarena.com';

export const PRIVACY_POLICY: LegalDocument = {
  path: '/privacy-policy',
  title: 'Privacy Policy',
  metaTitle: 'Privacy Policy | SavingArena',
  metaDescription:
    'What SavingArena collects, why we hold it, who else sees it and how to have it removed. Written from what the site actually does.',
  updated: LAST_UPDATED,
  updatedIso: LAST_UPDATED_ISO,
  intro:
    'This explains what SavingArena collects, why we hold it, who else sees it and how to have it removed. It describes what the site actually does rather than what a template says it might.',
  sections: [
    {
      heading: 'What we collect',
      body: [
        'Almost everything here can be read without giving us anything at all. You do not need an account to browse stores, read coupons or follow a link to a merchant. We collect personal information only where you hand it to us, and in a few places where the site needs to tell one visitor apart from another.',
        'When you choose to send it to us:',
        {
          // ContactMessage, NewsletterSubscriber, BrandRequest, BlogComment, StoreFollower.
          list: [
            'Contact form: your name, email address, subject and message, plus the page you sent it from.',
            'Newsletter: your email address, and the page you subscribed from.',
            'Following a store: your email address, so we can tell you when that store posts a new offer.',
            'Blog comments: the name and email address you enter, and your comment.',
            'Add your brand: the business and contact details on that form, and any logo you upload.',
          ],
        },
        'Collected automatically:',
        {
          // StoreRating, StoreComparisonVote, BlogComment, NewsletterSubscriber, StoreFollower.
          list: [
            'Your IP address, when you rate a store, vote in a store comparison, post a comment, subscribe to the newsletter or follow a store. We record it to stop the same person voting repeatedly and to limit spam, not to build a profile of you.',
            'Your browser and device details, known as the user agent, alongside comments and newsletter signups, for the same reason.',
            'Analytics about how the site is used, described further down.',
          ],
        },
        'An IP address counts as personal data under UK and EU law even though it does not carry your name. That is why it is listed here rather than treated as anonymous.',
      ],
    },
    {
      heading: 'Analytics and advertising',
      body: [
        'Two Google services run on this site.',
        {
          list: [
            'Google Analytics 4 tells us which pages are read, how people move between them and which offers get clicked. It is how we know what is worth writing more of.',
            'Google AdSense serves the advertising that pays for the site. Google may use cookies to decide which ads you see.',
          ],
        },
        'Both are set by Google rather than by us, and both are governed by Google’s own privacy terms as well as this policy. Which of them run depends on the choice you make in the cookie banner. The Cookie Policy explains what each category does and how to change your mind later.',
      ],
    },
    {
      heading: 'Affiliate links',
      body: [
        'SavingArena earns a commission when you buy through some of the links on this site. Clicking an offer sends you through a redirect on our own domain and then on to the merchant, or to the affiliate network that tracks the sale.',
        'That network usually sets a cookie in your browser so a purchase can be credited back to us. We never see your order, your payment details or what you bought. All that reaches us is whether a sale happened and what it earned. The commission does not change the price you pay.',
      ],
    },
    {
      heading: 'Why we are allowed to hold it',
      body: [
        'Where you gave us something so we could do a thing you asked for, such as answering a message, sending the newsletter or telling you about a store, we rely on your consent. You can withdraw it at any time.',
        'Where we record an IP address to stop vote rigging and spam, we rely on legitimate interests. The site does not work fairly without it, and it is the least we can hold to achieve that.',
        'Analytics and advertising cookies run on consent, which is what the banner asks for.',
      ],
    },
    {
      heading: 'Who else sees it',
      body: [
        'We do not sell personal information, and we do not share it for anyone else’s marketing.',
        'It reaches other companies only where the service genuinely needs it. Google, for analytics and advertising. Affiliate networks, for crediting a sale. Our email provider, for sending what you asked to receive. Our hosting provider, which stores the data on our behalf. Some of these operate outside the UK and EU.',
      ],
    },
    {
      heading: 'How long we keep it',
      body: [
        'Contact messages and brand requests are kept while they are being dealt with, and for a reasonable period afterwards in case the conversation resumes.',
        'Newsletter and store follow subscriptions are kept until you unsubscribe. Comments stay for as long as the article does.',
        'Analytics data is retained for the period set in our Google Analytics property.',
      ],
    },
    {
      heading: 'Your rights',
      body: [
        'If you are in the UK or the EU you can ask us for a copy of what we hold about you, ask us to correct it, ask us to delete it, or object to us holding it at all. You can also complain to your national data protection authority.',
        `Write to ${CONTACT_EMAIL} and say what you want. We will not ask you to justify the request, and there is no charge for it.`,
        'Every newsletter carries an unsubscribe link, which is faster than emailing us.',
      ],
    },
    {
      heading: 'Children',
      body: [
        'SavingArena is not aimed at children and we do not knowingly collect anything from anyone under 16. If you believe a child has sent us something, tell us and we will remove it.',
      ],
    },
    {
      heading: 'Changes, and how to reach us',
      body: [
        'If this policy changes in a way that affects you, the date at the top of this page changes with it.',
        `Questions about any of this go to ${CONTACT_EMAIL}.`,
      ],
    },
  ],
};

export const COOKIE_POLICY: LegalDocument = {
  path: '/cookie-policy',
  title: 'Cookie Policy',
  metaTitle: 'Cookie Policy | SavingArena',
  metaDescription:
    'The cookies SavingArena and its partners set, what each category is actually for, and how to change your choice at any time.',
  updated: LAST_UPDATED,
  updatedIso: LAST_UPDATED_ISO,
  intro:
    'A cookie is a small file a site asks your browser to keep, so something can be remembered between one page and the next. This lists the ones used here, grouped by what they are for.',
  sections: [
    {
      heading: 'Strictly necessary',
      body: [
        'These make the site work and cannot be switched off. They remember the cookie choice you made, so the banner does not ask again on every page, and they keep the site secure.',
        'Nothing in this group is used to track you or to build a profile, and none of it is shared with anyone.',
      ],
    },
    {
      heading: 'Analytics',
      body: [
        'Set by Google Analytics 4. They tell us which pages are read, how people move through the site and which offers are clicked, in aggregate rather than as a named individual.',
        'This is how we know which guides are worth writing more of and which pages are quietly failing. Turning them off changes nothing you can see.',
      ],
    },
    {
      heading: 'Advertising',
      body: [
        'Set by Google AdSense, which serves the advertising that pays for this site. Google may use them to decide which ads you are shown and to avoid repeating the same one.',
        'Turning them off does not remove the advertising. It makes it less relevant to you.',
      ],
    },
    {
      heading: 'Affiliate tracking',
      body: [
        'When you click through to a merchant, the affiliate network that tracks the sale usually sets a cookie so any purchase can be credited back to us. That cookie is set by the network, on its own domain, after you have already left this site. It is what makes SavingArena free to use.',
        'It records that a click came from us. It does not record what you looked at, added to a basket or paid.',
      ],
    },
    {
      heading: 'Changing your mind',
      body: [
        'Use the Cookie settings link in the footer to reopen the banner and change your choice. It takes effect immediately and applies from that point on.',
        'Your browser can also block or delete cookies for any site, including this one. Doing that for the strictly necessary group will make the banner ask again on every visit.',
      ],
    },
  ],
};

export const TERMS_OF_SERVICE: LegalDocument = {
  path: '/terms',
  title: 'Terms of Service',
  metaTitle: 'Terms of Service | SavingArena',
  metaDescription:
    'The terms for using SavingArena: what we promise about coupons, how our affiliate links work, and what you agree to when you post something here.',
  updated: LAST_UPDATED,
  updatedIso: LAST_UPDATED_ISO,
  intro:
    'These are the terms for using SavingArena. They are written to be read rather than skipped, so they are shorter and plainer than most.',
  sections: [
    {
      heading: 'What SavingArena is',
      body: [
        'SavingArena is a free site that lists coupons, promo codes and deals for online stores. We are not a shop. Everything you buy is bought from the merchant, on their site, under their terms.',
        'You do not need an account to use any of it. Browsing, reading and following a link to a store all require nothing from you.',
      ],
    },
    {
      heading: 'What we promise about the offers',
      body: [
        'We check codes before we publish them, and we remove the ones we find have stopped working. That is a real effort, and it is also the limit of what anyone can honestly promise. A merchant can withdraw an offer, change its conditions, run out of stock or restrict it to certain customers at any moment, without telling us.',
        'So an offer here is our best information at the time it was published, not a guarantee. If a code does not work at checkout, the merchant’s terms are what apply, not ours.',
        'Discounts, prices and expiry dates are shown as the merchant states them. Where a price appears it is the merchant’s price, and it can change before you reach their checkout.',
      ],
    },
    {
      heading: 'How we make money',
      body: [
        'Many of the links here are affiliate links. If you buy something after following one, the merchant or their affiliate network usually pays us a commission.',
        'It never changes what you pay, and it does not decide what we publish or how we rank it. An offer is listed because it is worth listing. A merchant cannot buy a better placement by paying more.',
      ],
    },
    {
      heading: 'Other companies’ sites',
      body: [
        'Following an offer takes you to a merchant we do not own, control or run. What happens after that, including the order, the payment, the delivery and any return, is between you and them, governed by their terms and their privacy policy.',
        'We are not responsible for what a merchant does, and we cannot resolve a dispute with one on your behalf. Contact them directly.',
      ],
    },
    {
      heading: 'What you post here',
      body: [
        'You can submit a coupon, comment on an article, rate a store, or ask us to add your brand. When you do:',
        {
          list: [
            'You keep ownership of what you wrote. You give us permission to publish it on this site and to edit it for length, clarity or accuracy.',
            'You confirm it is yours to post, that it is not someone else’s copyrighted work, and that it is not misleading, abusive, unlawful or spam.',
            'We can decline to publish anything, and remove anything already published, without giving a reason.',
          ],
        },
        'Please do not post personal information about anyone else, including your own contact details in a public comment.',
      ],
    },
    {
      heading: 'Using the site fairly',
      body: [
        'You are welcome to read anything here, save it, and share a link to it.',
        'Not permitted: scraping or bulk copying the listings, republishing our content as your own, interfering with the site or the servers behind it, or using automated tools to submit content, inflate ratings or manipulate votes.',
        'The site’s design, its wording and the way its listings are compiled belong to us. Store names, logos and trade marks belong to the merchants they identify, and appear here to say which store an offer is for.',
      ],
    },
    {
      heading: 'What we do not promise',
      body: [
        'We publish this site as it is. We do not promise it will be available without interruption, free of errors, or that every listing will be current at the moment you read it.',
        'Nothing here is financial or purchasing advice. Decide for yourself whether an offer is worth taking.',
        'So far as the law allows, we are not liable for a loss that follows from using this site, including an offer that did not work, a price that had changed, or a merchant that did not deliver. Nothing in these terms limits liability where the law does not allow it to be limited.',
      ],
    },
    {
      heading: 'Changes',
      body: [
        'We update these terms when the site changes. The date at the top of this page changes with them, and continuing to use the site after that means the new version applies.',
        'We may also change, suspend or withdraw any part of the site.',
      ],
    },
    {
      heading: 'Getting in touch',
      body: [
        `Questions about these terms, or about something published here, go to ${CONTACT_EMAIL}.`,
        'If you believe something on this site infringes your copyright or trade mark, write to the same address with enough detail to identify it and we will look into it.',
      ],
    },
  ],
};

export const AFFILIATE_DISCLOSURE: LegalDocument = {
  path: '/affiliate-disclosure',
  title: 'Affiliate Disclosure',
  metaTitle: 'Affiliate Disclosure | SavingArena',
  metaDescription:
    'How SavingArena earns money, what a commission does and does not influence, and why it never changes the price you pay.',
  updated: LAST_UPDATED,
  updatedIso: LAST_UPDATED_ISO,
  intro:
    'SavingArena is free to use because merchants pay us when a reader buys through one of our links. This page says plainly how that works, and what it does not affect.',
  sections: [
    {
      heading: 'How we earn',
      body: [
        'Many of the offers on this site are affiliate links. When you follow one and go on to buy something, the merchant, or the affiliate network working on their behalf, pays us a small share of the sale.',
        'The link runs through a redirect on our own domain before it reaches the merchant. That is how the click is counted, and it is the same redirect whether or not the offer earns us anything.',
      ],
    },
    {
      heading: 'What it costs you',
      body: [
        'Nothing. The price you pay is the merchant’s price. A commission comes out of what the merchant already earns on the sale, so nothing is added to your total for having arrived through us.',
        'You are also free to ignore our links entirely and go to the merchant directly. The offer will be the same. We would rather you found the discount than that we were paid for it.',
      ],
    },
    {
      heading: 'What it does not influence',
      body: [
        'A commission does not decide what appears on this site or where it appears.',
        {
          list: [
            'We list offers from merchants who pay us nothing at all, because a reader looking for that store still needs the answer.',
            'A merchant cannot buy a higher position, a verified badge or a better rating. None of those are for sale.',
            'When an offer stops working we remove it, whether or not it was one of the earning ones.',
          ],
        },
        'If we ever accept payment for placing something, that placement will say so on the page itself, not only here.',
      ],
    },
    {
      heading: 'Where the money goes',
      body: [
        'Commission is what pays for checking codes, writing the guides and running the site. There is no subscription, no paywall, and no charge to readers for anything on SavingArena.',
      ],
    },
    {
      heading: 'Questions',
      body: [
        `If you want to know whether a particular link earns us anything, ask. Write to ${CONTACT_EMAIL} and we will tell you.`,
      ],
    },
  ],
};

export const ACCESSIBILITY_STATEMENT: LegalDocument = {
  path: '/accessibility',
  title: 'Accessibility',
  metaTitle: 'Accessibility | SavingArena',
  metaDescription:
    'How SavingArena is built to be usable with a keyboard, a screen reader or a zoomed page, what we know is imperfect, and how to report a problem.',
  updated: LAST_UPDATED,
  updatedIso: LAST_UPDATED_ISO,
  intro:
    'A coupon is only useful if you can reach it. This page says how the site is built to be usable, what we already know is imperfect, and how to tell us when something gets in your way.',
  sections: [
    {
      heading: 'What we aim for',
      body: [
        'We build towards the Web Content Accessibility Guidelines version 2.1 at level AA. That is the standard most public bodies are held to, and it is a reasonable bar for a site anyone might land on from a search result.',
        'We are not claiming full conformance. Claiming it without an audit to back it up would be a promise we could not keep.',
      ],
    },
    {
      heading: 'What is already in place',
      body: [
        {
          list: [
            'Every page is delivered as real HTML from the server, so it works before any script has run, and with a screen reader that reads the document rather than the rendered view.',
            'Headings follow a real order on each page, so a screen reader’s heading list is a usable table of contents rather than a flat run of text.',
            'Interactive things are buttons and links rather than styled boxes, so they can be reached with the Tab key and used with Enter or Space.',
            'Images carry alternative text, and decorative ones are hidden from screen readers rather than read out as noise.',
            'The site reflows to a narrow window and to a zoomed page without content being cut off or trapped in a sideways scroll.',
            'Motion is kept small, and animation is reduced for anyone whose system asks for that.',
          ],
        },
      ],
    },
    {
      heading: 'What we know is imperfect',
      body: [
        'Some of what appears here comes from merchants and affiliate feeds rather than from us. Where a merchant supplies a logo or a product image with no useful description, we fall back to the store or product name. That is honest, but it is not always as descriptive as text we wrote ourselves.',
        'Longer listing pages carry a lot of links. We are working on reducing how much a keyboard user has to move through before reaching the offers.',
        'Advertising is served by Google and rendered inside frames we do not control, so its accessibility is not ours to fix.',
      ],
    },
    {
      heading: 'Copying text',
      body: [
        'Selecting text with a mouse is restricted on this site. That restriction is deliberate, and it never applies to assistive technology. A screen reader reads the page as normal, the content is present in the HTML for any tool that reads it, and every form field stays fully selectable and editable.',
      ],
    },
    {
      heading: 'Telling us about a problem',
      body: [
        `If something here is hard or impossible to use, we want to hear about it. Write to ${CONTACT_EMAIL} and describe what you were trying to do, which page you were on, and what happened instead. Mentioning your browser and any assistive software helps us reproduce it.`,
        'We read every one of these, and we will tell you what we find.',
      ],
    },
  ],
};

export const LEGAL_DOCUMENTS = [
  PRIVACY_POLICY,
  COOKIE_POLICY,
  TERMS_OF_SERVICE,
  AFFILIATE_DISCLOSURE,
  ACCESSIBILITY_STATEMENT,
];
